import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, PackagePlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatNumber } from '../../../components/design-system/admin';
import { ImportSkipReason, NetworkAllowance, NetworkImportResult, useNetworkApi } from '../../../common/hooks/use-network-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { skipReasonLabel } from './network-labels';

const SHOWN_LINKS = 8;

/**
 * "Add to my catalogue": how many, what the plan allows, then what happened.
 *
 * The plan line is shown before anything is added, so a partial import is
 * something the retailer was told about rather than a surprise in the
 * result. The result lists what was added (with links to the new products)
 * and, by reason, what was not — never a bare "done".
 */
export const ImportDialog: React.FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    uuids: string[];
    brandName?: string;
    allowance: NetworkAllowance | null;
    onImported: (result: NetworkImportResult) => void;
}> = ({ open, onOpenChange, uuids, brandName, allowance, onImported }) => {
    const { t } = useTranslation();
    const api = useNetworkApi();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<NetworkImportResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setResult(null);
            setError(null);
        }
    }, [open]);

    const count = uuids.length;
    const unlimited = !allowance || allowance.remaining === null || allowance.limit === -1;
    const tight = !unlimited && (allowance!.remaining ?? 0) < count;

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            const r = await api.importProducts(uuids);
            setResult(r);
            onImported(r);
            if (r.created > 0) toast.success(t('NETWORK_RETAILER.IMPORT.TOAST', { count: r.created }));
        } catch (e) {
            setError((await readApiError(e)).message || t('NETWORK_RETAILER.ERROR'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
            <DialogContent className="gap-5 sm:max-w-[560px]" data-testid="network-import-dialog">
                {!result ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t('NETWORK_RETAILER.IMPORT.TITLE', { count, formatted: formatNumber(count) })}</DialogTitle>
                            <DialogDescription>
                                {brandName
                                    ? t('NETWORK_RETAILER.IMPORT.DESCRIPTION_BRAND', { brand: brandName })
                                    : t('NETWORK_RETAILER.IMPORT.DESCRIPTION')}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="rounded-lg border p-3 text-sm" data-testid="network-import-plan">
                            <p className="font-medium">{t('NETWORK_RETAILER.IMPORT.PLAN')}</p>
                            <p className="mt-0.5 text-muted-foreground">
                                {unlimited
                                    ? t('NETWORK_RETAILER.IMPORT.PLAN_UNLIMITED')
                                    : t('NETWORK_RETAILER.IMPORT.PLAN_ROOM', {
                                          used: formatNumber(allowance!.used),
                                          limit: formatNumber(allowance!.limit),
                                          remaining: formatNumber(allowance!.remaining ?? 0),
                                      })}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">{t('NETWORK_RETAILER.IMPORT.PLAN_VARIANTS')}</p>
                            {tight && (
                                <p className="mt-2 text-xs text-warning" data-testid="network-import-tight">
                                    {t('NETWORK_RETAILER.IMPORT.PLAN_TIGHT', { remaining: formatNumber(allowance!.remaining ?? 0) })}
                                </p>
                            )}
                        </div>

                        {error && <p className="text-sm text-destructive">{error}</p>}

                        <DialogFooter>
                            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                                {t('NETWORK_RETAILER.CANCEL')}
                            </Button>
                            <Button variant="outline" onClick={submit} disabled={busy || !count} data-testid="network-import-submit">
                                {busy ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <PackagePlus data-icon="inline-start" />}
                                {busy ? t('NETWORK_RETAILER.IMPORT.ADDING') : t('NETWORK_RETAILER.IMPORT.SUBMIT')}
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <ImportResultView result={result} onClose={() => onOpenChange(false)} />
                )}
            </DialogContent>
        </Dialog>
    );
};

const ImportResultView: React.FC<{ result: NetworkImportResult; onClose: () => void }> = ({ result, onClose }) => {
    const { t } = useTranslation();
    const created = result.items.filter((i) => i.status === 'created');
    const failed = result.items.filter((i) => i.status === 'failed');
    const reasons = Object.entries(result.skippedByReason) as Array<[ImportSkipReason, number]>;

    return (
        <>
            <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                    {result.created > 0 && <CheckCircle2 className="size-5 text-success" aria-hidden />}
                    {result.created > 0
                        ? t('NETWORK_RETAILER.IMPORT.DONE_TITLE', { count: result.created, formatted: formatNumber(result.created) })
                        : t('NETWORK_RETAILER.IMPORT.NOTHING_TITLE')}
                </DialogTitle>
                <DialogDescription>
                    {result.created > 0
                        ? t('NETWORK_RETAILER.IMPORT.DONE_HINT', { products: formatNumber(result.createdProducts) })
                        : t('NETWORK_RETAILER.IMPORT.NOTHING_HINT')}
                </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-3 text-sm" data-testid="network-import-result">
                {reasons.length > 0 && (
                    <ul className="flex flex-col gap-1 rounded-lg border p-3">
                        {reasons.map(([reason, n]) => (
                            <li key={reason} className="flex justify-between gap-3">
                                <span className="text-muted-foreground">{skipReasonLabel(t, reason)}</span>
                                <span className="tabular-nums">{formatNumber(n)}</span>
                            </li>
                        ))}
                    </ul>
                )}
                {failed.length > 0 && (
                    <div className="rounded-lg border border-destructive/30 p-3 text-destructive">
                        <p>{t('NETWORK_RETAILER.IMPORT.FAILED', { count: failed.length })}</p>
                        <ul className="mt-1 list-disc ps-5 text-xs">
                            {failed.slice(0, 5).map((f) => (
                                <li key={f.networkProductUuid}>
                                    {f.name || f.networkProductUuid}: {f.message}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {created.length > 0 && (
                    <div>
                        <p className="text-xs text-muted-foreground">{t('NETWORK_RETAILER.IMPORT.NEW_PRODUCTS')}</p>
                        <ul className="mt-1 flex flex-col gap-1">
                            {created.slice(0, SHOWN_LINKS).map((c) => (
                                <li key={c.networkProductUuid}>
                                    <Link to={`/products/${c.productUuid}/edit`} className="hover:underline" onClick={onClose}>
                                        {c.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        {created.length > SHOWN_LINKS && (
                            <p className="mt-1 text-xs text-muted-foreground">{t('NETWORK_RETAILER.IMPORT.AND_MORE', { count: created.length - SHOWN_LINKS })}</p>
                        )}
                    </div>
                )}
            </div>

            <DialogFooter>
                {created.length > 0 && (
                    <Button variant="outline" asChild>
                        <Link to="/products/catalog" onClick={onClose} data-testid="network-import-open-catalog">
                            {t('NETWORK_RETAILER.IMPORT.OPEN_CATALOG')}
                        </Link>
                    </Button>
                )}
                <Button variant="outline" onClick={onClose} data-testid="network-import-close">
                    {t('NETWORK_RETAILER.CLOSE')}
                </Button>
            </DialogFooter>
        </>
    );
};
